import type { StateCreator } from "zustand";
import type { RunControlState } from "./index";

export interface FramesSlice {
  activeNodeIds: Set<string>;
  runningNodeIds: Set<string>;
  waitingNodeIds: Set<string>;
  suspendedNodeIds: Set<string>;
  completedNodeIds: Set<string>;
  failedNodeIds: Set<string>;
  nodeAttemptCounts: Map<string, number>;
  nodeSuccessCounts: Map<string, number>;
  nodeFailureCounts: Map<string, number>;
  setRunning: (ids: Iterable<string>) => void;
  setWaiting: (ids: Iterable<string>) => void;
  setSuspended: (ids: Iterable<string>) => void;
  addCompleted: (nodeId: string) => void;
  addFailed: (nodeId: string) => void;
  incAttempt: (nodeId: string, delta?: number) => void;
  incSuccess: (nodeId: string, delta?: number) => void;
  incFailure: (nodeId: string, delta?: number) => void;
  clearFrames: () => void;
}

export const createFramesSlice: StateCreator<
  RunControlState,
  [["zustand/immer", never], ["zustand/subscribeWithSelector", never]],
  [],
  FramesSlice
> = (set) => ({
  activeNodeIds: new Set(),
  runningNodeIds: new Set(),
  waitingNodeIds: new Set(),
  suspendedNodeIds: new Set(),
  completedNodeIds: new Set(),
  failedNodeIds: new Set(),
  nodeAttemptCounts: new Map(),
  nodeSuccessCounts: new Map(),
  nodeFailureCounts: new Map(),

  setRunning: (ids) =>
    set((state) => {
      const next = new Set(ids);
      state.runningNodeIds = next;
      state.activeNodeIds = next;
    }),

  setWaiting: (ids) =>
    set((state) => {
      state.waitingNodeIds = new Set(ids);
    }),

  setSuspended: (ids) =>
    set((state) => {
      state.suspendedNodeIds = new Set(ids);
    }),

  addCompleted: (nodeId) =>
    set((state) => {
      state.completedNodeIds.add(nodeId);
    }),

  addFailed: (nodeId) =>
    set((state) => {
      state.failedNodeIds.add(nodeId);
    }),

  incAttempt: (nodeId, delta = 1) =>
    set((state) => {
      state.nodeAttemptCounts.set(nodeId, (state.nodeAttemptCounts.get(nodeId) ?? 0) + delta);
    }),

  incSuccess: (nodeId, delta = 1) =>
    set((state) => {
      state.nodeSuccessCounts.set(nodeId, (state.nodeSuccessCounts.get(nodeId) ?? 0) + delta);
    }),

  incFailure: (nodeId, delta = 1) =>
    set((state) => {
      state.nodeFailureCounts.set(nodeId, (state.nodeFailureCounts.get(nodeId) ?? 0) + delta);
    }),

  clearFrames: () =>
    set((state) => {
      state.activeNodeIds = new Set();
      state.runningNodeIds = new Set();
      state.waitingNodeIds = new Set();
      state.suspendedNodeIds = new Set();
      state.completedNodeIds = new Set();
      state.failedNodeIds = new Set();
      state.nodeAttemptCounts = new Map();
      state.nodeSuccessCounts = new Map();
      state.nodeFailureCounts = new Map();
    }),
});
